/**
 * SignalR Socket Context for Real-time Notifications
 * 
 * This context manages the SignalR connection to the notification hub.
 * 
 * Hub URL: Uses VITE_API_BASE_URL environment variable + /notificationHub
 * Test API URL: Uses VITE_API_BASE_URL environment variable + /Health/sendSignalRNotification
 * 
 * Endpoints:
 * - SignalR Hub: wss://[your-server]/notificationHub (for real-time notifications)
 * - Test API: POST [your-server]/api/Health/sendSignalRNotification (for sending test notifications)
 * 
 * Environment Configuration:
 * - Set VITE_API_BASE_URL in your .env file (e.g., https://your-dev-server.com/api)
 * - SignalR will automatically use the correct URLs based on this environment variable
 * 
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Authentication via Bearer token
 * - Real-time notification events
 * - Connection status tracking
 * - Error handling and logging
 * 
 * Events Handled:
 * - ReceiveNotification: New notification received
 * - ReceiveNotificationCount: Unread count updated
 * 
 * Usage:
 * 1. Connection is automatically established when user is authenticated
 * 2. Client automatically joins "JoinedNotificationGroup"
 * 3. Notifications are received in real-time and trigger UI updates
 * 4. Connection status is displayed in the notification dropdown
 * 
 * Troubleshooting WebSocket Connection Issues:
 * 
 * Common Error: "WebSocket failed to connect. The connection could not be found on the server"
 * 
 * Possible Causes & Solutions:
 * 1. Wrong URL: Check VITE_API_BASE_URL environment variable
 *    - Should point to your SignalR server (e.g., https://your-server.com/api)
 *    - Hub URL will be: https://your-server.com/notificationHub
 * 
 * 2. Server not running: Ensure SignalR server is running and accessible
 *    - Test with: curl [your-server]/notificationHub/negotiate
 *    - Test notification endpoint: POST [your-server]/api/Health/sendSignalRNotification
 *    - URLs are constructed from VITE_API_BASE_URL environment variable
 * 
 * 3. Authentication issues: Check if Bearer token is valid
 *    - Token should be in localStorage as 'access_token'
 *    - Server should accept the token format
 * 
 * 4. CORS issues: Server must allow WebSocket connections from your domain
 *    - Check server CORS configuration
 * 
 * 5. Proxy/Firewall: Corporate networks may block WebSocket connections
 *    - Try Long Polling fallback (automatically enabled)
 * 
 * 6. SSL/TLS issues: Mixed content or certificate problems
 *    - Ensure HTTPS for production
 *    - Check certificate validity
 * 
 * Debug Steps:
 * 1. Check browser console for detailed error messages
 * 2. Use "Diagnose" button in notification dropdown
 * 3. Check Network tab for failed WebSocket connections
 * 4. Verify environment variables are loaded correctly
 * 5. Test API endpoints manually
 */

import React, { createContext, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNotificationHub } from '../hooks/useNotificationHub';
import { useAuth } from './AuthContext';
import { getNormalizedApiBaseUrl } from '../config/api';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Get access token from localStorage
  const accessToken = localStorage.getItem('access_token');

  // Callback to invalidate React Query cache when new notification arrives
  const handleNotificationReceived = (notification) => {

    // Invalidate and refetch notifications and unread count
    queryClient.invalidateQueries({
      queryKey: ['notifications'],
    });
    queryClient.invalidateQueries({
      queryKey: ['unreadCount'],
    });
  };

  // Use the new notification hub hook
  const {
    connection,
    isConnected,
    connectionError,
    messages,
    sendTestNotification: hookSendTestNotification,
    refreshNotifications: hookRefreshNotifications,
    getConnectionStatus,
    reconnect: hookReconnect,
    testServerConnectivity
  } = useNotificationHub(accessToken, handleNotificationReceived);

  // Enhanced refresh function that also invalidates React Query cache
  const refreshNotifications = () => {

    hookRefreshNotifications();

    // Also invalidate React Query cache for notifications
    queryClient.invalidateQueries({
      queryKey: ['notifications'],
    });
    queryClient.invalidateQueries({
      queryKey: ['unreadCount'],
    });
  };

  // Function to manually reconnect SignalR
  const reconnectSignalR = async () => {

    await hookReconnect();
  };

  // Enhanced test notification function
  const sendTestNotification = async (customUserId = null) => {
    try {
      // Get the actual user ID from authentication context
      let actualUserId = customUserId || user?.id;

      // Fallback: get user ID directly from sessionStorage if not in context
      if (!actualUserId) {
        try {
          const savedUser = sessionStorage.getItem('inchaa_user');
          if (savedUser) {
            const userData = JSON.parse(savedUser);
            actualUserId = userData.id;

          }
        } catch (e) {
          console.warn('⚠️ Error parsing user from sessionStorage:', e);
        }
      }

      if (!actualUserId) {
        console.error('❌ No user ID available for test notification');
        console.error('❌ User object from context:', user);
        console.error('❌ SessionStorage user:', sessionStorage.getItem('inchaa_user'));
        throw new Error('No user ID available. Please ensure you are logged in.');
      }




      // Use the hook's sendTestNotification function
      await hookSendTestNotification(actualUserId);

      // Refresh notifications after sending test
      refreshNotifications();

    } catch (error) {
      console.error('❌ Error sending test notification:', error);
    }
  };


  // Function to test the exact request being sent (for debugging)
  const debugTestNotification = () => {
    const actualUserId = user?.id;
    const apiBaseUrl = getNormalizedApiBaseUrl();
    const testNotificationUrl = `${apiBaseUrl}/Health/sendSignalRNotification`;
    const requestPayload = { userId: actualUserId };


    return {
      url: testNotificationUrl,
      userId: actualUserId,
      payload: requestPayload,
      hasToken: !!accessToken
    };
  };

  // Function to test connection and provide diagnostics
  const testConnection = async () => {
    // Test the actual notification endpoint
    try {
      // Hardcode userId to 1 (number) for diagnose function
      const actualUserId = 1;
      await sendTestNotification(actualUserId);
    } catch (apiError) {
      console.warn('⚠️ Notification test failed:', apiError.message);
    }

    return getConnectionStatus();
  };

  const value = {
    connection,
    isConnected,
    connectionError,
    messages,
    refreshNotifications,
    sendTestNotification,
    getConnectionStatus,
    testConnection,
    reconnectSignalR,
    debugTestNotification,
    testServerConnectivity,
  };

  // Make testConnection available globally for debugging
  if (typeof window !== 'undefined') {
    window.testConnectionDebug = testConnection;
    window.debugTestNotification = debugTestNotification;
    window.testServerConnectivity = testServerConnectivity;
  }

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
